import { Fragment } from "react";
import classNames from "classnames";
import { ChevronRightSmallIcon } from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";

const ANCESTOR_LABELS = ["Reports", "Cash flow"];
const CURRENT_LABEL = "Operating activities";

/**
 * X-ray plate for Breadcrumbs: ancestors, separators and the current item.
 * The variant id is Figma's `Levels`, so it picks how many levels to draw.
 */
export const BreadcrumbsSchematic = ({
    bindings,
    highlight,
    variantId,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    variantId: string;
}) => {
    const read = bindingReader(bindings);
    const ancestorLabels = ANCESTOR_LABELS.slice(0, Number(variantId) - 1);
    const textStyle = (partId: "itemLabel" | "label") => ({
        color: read(partId, "color"),
        fontFamily: read(partId, "font-family"),
        fontSize: read(partId, "font-size"),
        fontWeight: read(partId, "font-weight"),
        lineHeight: read(partId, "line-height"),
        letterSpacing: read(partId, "letter-spacing"),
    });

    return (
        <span className={classNames(styles.schematic, styles.buttonSchematic)}>
            <Part
                partId="surface"
                highlight={highlight}
                className={styles.buttonBackground}
            />
            <span
                className={classNames(styles.buttonContent, styles.linkContent)}
                style={{ gap: read("surface", "gap") }}
            >
                {ancestorLabels.map((label) => (
                    <Fragment key={label}>
                        <Part
                            partId="itemLabel"
                            highlight={highlight}
                            className={styles.buttonLabel}
                            style={textStyle("itemLabel")}
                        >
                            {label}
                        </Part>
                        <Part
                            partId="separator"
                            highlight={highlight}
                            className={styles.linkGlyph}
                            style={{
                                width: read("separator", "width"),
                                height: read("separator", "height"),
                                color: read("separator", "color"),
                            }}
                        >
                            <ChevronRightSmallIcon />
                        </Part>
                    </Fragment>
                ))}
                <Part
                    partId="label"
                    highlight={highlight}
                    className={styles.buttonLabel}
                    style={textStyle("label")}
                >
                    {CURRENT_LABEL}
                </Part>
            </span>
        </span>
    );
};
